import { useCallback, useEffect, useState } from "react"
import { useDebouncedProperty } from "./useDebouncedProperty"

interface ElementSizeData {
  width: number | null
  height: number | null
  element: Element | null
}

type ElementsSizes = Record<string, ElementSizeData>

export const useElementsResizeListen = (
  elementIds: string[]
): { elementsSizes: ElementsSizes; debouncedElementsSizes: ElementsSizes } => {
  const [elementsSizes, setElementsSizes] = useState<ElementsSizes>(() =>
    elementIds.reduce((res, id) => {
      res[id] = {
        width: null,
        height: null,
        element: null
      }
      return res
    }, {} as ElementsSizes)
  )
  const [debouncedElementsSizes] = useDebouncedProperty(elementsSizes)

  const elementsSizesSetter = useCallback((element: Element) => {
    const id = element.id
    const { width, height } = element.getBoundingClientRect()

    const roundedWidth = Number((width || 0).toFixed(1))
    const roundedHeight = Number((height || 0).toFixed(1))

    setElementsSizes(prevState => {
      const prevElementSizes = prevState[id]

      if (
        prevElementSizes?.width === roundedWidth &&
        prevElementSizes?.height === roundedHeight
      ) {
        return prevState
      }

      return {
        ...prevState,
        [id]: { width: roundedWidth, height: roundedHeight, element }
      }
    })
  }, [])

  useEffect(() => {
    const elements = elementIds
      ?.map(id => document.getElementById(id))
      ?.filter(element => element !== null)

    if (!elements?.length) {
      return
    }

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        elementsSizesSetter(entry.target)
      })
    })

    elements.forEach(element => {
      resizeObserver.observe(element)
    })

    elements.forEach(element => {
      elementsSizesSetter(element)
    })

    return () => {
      resizeObserver.disconnect()
    }
  }, [elementIds, elementsSizesSetter])

  return { elementsSizes, debouncedElementsSizes }
}
